import React, { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
// @ts-ignore
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import { View, Text, StyleSheet, Platform, TouchableOpacity } from 'react-native';
import { Button, NumberInput } from '@app/components'; // NumberInput ici pour la durée
import ReactDOM from 'react-dom';
import 'mapbox-gl/dist/mapbox-gl.css';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import { useAppSelector } from '@app/store/hooks';
import { RootState } from '@app/store/store';
import * as turf from '@turf/turf';
import { theme } from '@app/theme';
import { getEnv } from '@app/environment';

type PolygonLocation = {
    type: 'Polygon';
    coordinates: number[][][];
};

interface DeliveryZoneMapModalProps {
    isOpen: boolean;
    onClose: () => void;
    initialData: PolygonLocation | null;
    onSave: (data: PolygonLocation | null) => void;
    // Autres zones existantes (non modifiables)
    existingZones?: PolygonLocation[];
}

export const DeliveryZoneMapModal = ({ isOpen, onClose, initialData, onSave, existingZones }: DeliveryZoneMapModalProps) => {
    const mapContainer = useRef<HTMLDivElement>(null);
    const map = useRef<mapboxgl.Map | null>(null);
    const draw = useRef<MapboxDraw | null>(null);
    const selectedShop = useAppSelector((state: RootState) => state.app.selectedShop);

    // État pour le menu contextuel
    const [contextMenu, setContextMenu] = useState<{ x: number, y: number, featureId: string } | null>(null);

    // État pour la popover "Zone Auto"
    const [showIsochroneMenu, setShowIsochroneMenu] = useState(false);
    const [generationType, setGenerationType] = useState<'time' | 'distance'>('time');
    const [isochroneValue, setIsochroneValue] = useState('10'); // minutes ou km
    const [isochroneMode, setIsochroneMode] = useState<'driving' | 'cycling' | 'walking'>('driving');

    // État pour le mode de dessin
    const [drawMode, setDrawMode] = useState<'simple_select' | 'draw_polygon'>('simple_select');

    useEffect(() => {
        if (!isOpen) return;
        
        const apiKey = getEnv('MAPBOX_API_KEY');
        
        if (!apiKey) {
            console.error('MAPBOX_API_KEY is missing');
            return;
        }

        mapboxgl.accessToken = apiKey;

        if (map.current) return;

        setTimeout(() => {
            if(mapContainer.current) {
                // Centre par défaut : Paris
                let center: [number, number] = [2.3522, 48.8566];
                
                // Si shop sélectionné, on centre dessus
                if (selectedShop?.location?.coordinates) {
                    center = selectedShop.location.coordinates as [number, number];
                }

                 map.current = new mapboxgl.Map({
                    container: mapContainer.current,
                    style: 'mapbox://styles/mapbox/streets-v12',
                    center: center,
                    zoom: 13
                });
    
                draw.current = new MapboxDraw({
                    displayControlsDefault: false, // On gère nos propres contrôles
                    controls: {}, // Pas de contrôles par défaut
                    defaultMode: 'simple_select'
                });
    
                map.current.addControl(draw.current);
    
                map.current.on('load', () => {
                    // Si shop, on ajoute un marqueur
                    if (selectedShop?.location?.coordinates) {
                        new mapboxgl.Marker({ color: theme.colors.celadonblue })
                            .setLngLat(selectedShop.location.coordinates as [number, number])
                            .addTo(map.current!);
                    }

                    // Affichage des autres zones existantes en lecture seule
                    if (existingZones && existingZones.length > 0) {
                        const features: any[] = existingZones
                            .filter(z => z?.coordinates && z.coordinates.length > 0)
                            .map((loc, idx) => ({
                                type: 'Feature',
                                id: `existing-${idx}`,
                                properties: {},
                                geometry: {
                                    type: 'Polygon',
                                    coordinates: loc.coordinates,
                                }
                            }));

                        if (features.length > 0 && map.current?.getSource('existing-zones') === undefined) {
                            map.current!.addSource('existing-zones', {
                                type: 'geojson',
                                data: {
                                    type: 'FeatureCollection',
                                    features,
                                }
                            });

                            map.current!.addLayer({
                                id: 'existing-zones-fill',
                                type: 'fill',
                                source: 'existing-zones',
                                paint: {
                                    'fill-color': '#999999',
                                    'fill-opacity': 0.25,
                                },
                            });

                            map.current!.addLayer({
                                id: 'existing-zones-outline',
                                type: 'line',
                                source: 'existing-zones',
                                paint: {
                                    'line-color': '#999999',
                                    'line-width': 2,
                                },
                            });
                        }
                    }

                    if (initialData && initialData.coordinates && initialData.coordinates.length > 0) {
                         try {
                            const feature = {
                                id: 'initial-zone',
                                type: 'Feature',
                                properties: {},
                                geometry: {
                                    type: 'Polygon',
                                    coordinates: initialData.coordinates
                                }
                            };
                            // @ts-ignore
                            draw.current?.add(feature);
                            
                            // Fit bounds sur la zone existante
                            const coordinates = initialData.coordinates[0];
                            // @ts-ignore
                            const bounds = coordinates.reduce((bounds, coord) => {
                               return bounds.extend(coord as any);
                            }, new mapboxgl.LngLatBounds(coordinates[0], coordinates[0]));
                            
                            map.current?.fitBounds(bounds, { padding: 40 });
                         } catch (e) {
                             console.error('Error loading initial polygon', e);
                         }
                    }
                });

                // Écouter les changements de mode pour mettre à jour l'UI si le mode change automatiquement (ex: fin de tracé)
                map.current.on('draw.modechange', (e) => {
                    setDrawMode(e.mode);
                });

                // Gestion du clic pour sélection intelligente (plus petit en premier)
                map.current.on('click', (e) => {
                    setContextMenu(null); // Fermer menu si ouvert

                    // Si on est en mode sélection
                    if (draw.current.getMode() === 'simple_select') {
                        const features = map.current?.queryRenderedFeatures(e.point);
                        const drawFeatures = features?.filter(f => f.layer.id.includes('gl-draw-polygon-fill')); // On cible le remplissage

                        if (drawFeatures && drawFeatures.length > 1) {
                            // Plusieurs zones sous le clic
                            // On récupère les vraies features GeoJSON complètes via draw.get() car queryRenderedFeatures peut retourner des géométries coupées (tuiles)
                            const allDrawFeatures = drawFeatures.map(f => draw.current.get(f.properties?.id as string)).filter(Boolean);
                            
                            // Calculer l'aire de chaque feature
                            const sortedFeatures = allDrawFeatures.sort((a, b) => {
                                return turf.area(a as any) - turf.area(b as any);
                            });

                            // Sélectionner la plus petite
                            if (sortedFeatures.length > 0) {
                                // @ts-ignore
                                const smallestId = sortedFeatures[0].id;
                                draw.current.changeMode('simple_select', { featureIds: [smallestId] });
                            }
                        }
                    }
                });

                // Gestion du clic droit (context menu) sur la map
                map.current.on('contextmenu', (e) => {
                    // Vérifier si on a cliqué sur une feature
                    const features = map.current?.queryRenderedFeatures(e.point);
                    // Les features de draw ont souvent des ids spécifiques ou sont dans des layers spécifiques
                    // Mapbox Draw dessine dans des layers 'gl-draw-...'
                    const drawFeature = features?.find(f => f.layer.id.includes('gl-draw-polygon'));
                    
                    if (drawFeature && drawFeature.properties?.id) {
                        // Sélectionner la feature cliquée (utile pour que l'utilisateur voit sur quoi il agit)
                        draw.current.changeMode('simple_select', { featureIds: [drawFeature.properties.id] });

                        setContextMenu({
                            x: e.point.x,
                            y: e.point.y,
                            featureId: drawFeature.properties.id as string
                        });
                    } else {
                        setContextMenu(null);
                    }
                });
            }
        }, 100);
    }, [isOpen, initialData, selectedShop]);

    const changeMode = (mode: 'simple_select' | 'draw_polygon') => {
        if (draw.current) {
            draw.current.changeMode(mode);
            setDrawMode(mode);
        }
    };

    const handleSave = () => {
        const data = draw.current?.getAll();
        // @ts-ignore
        if (data && data.features.length > 0) {
            // Ne garder que les vraies zones (polygones)
            const polygonFeatures = data.features.filter(
                (f: any) =>
                    f?.geometry?.type === 'Polygon' ||
                    f?.geometry?.type === 'MultiPolygon'
            );

            // Aucune zone polygonale => on enregistre "sans zone"
            if (polygonFeatures.length === 0) {
                onSave(null);
                onClose();
                return;
            }

            // Plus d'une zone polygonale => on bloque et on affiche l'alerte
            if (polygonFeatures.length > 1) {
                alert("Vous ne pouvez valider qu'une seule zone. Veuillez supprimer les zones supplémentaires avant de valider.");
                return;
            }

            // À ce stade, on a exactement une seule zone polygonale
            let feature = polygonFeatures[0];

            // Ajuster automatiquement la zone pour qu'elle ne chevauche pas
            // les zones existantes : on soustrait les zones existantes au
            // polygone tracé, ce qui "clipse" la nouvelle zone sur les bords.
            if (existingZones && existingZones.length > 0 && feature?.geometry) {
                // @ts-ignore
                let result =
                    feature.geometry.type === 'Polygon'
                        ? turf.polygon(feature.geometry.coordinates as any)
                        : turf.multiPolygon((feature.geometry as any).coordinates);

                let changed = false;

                existingZones.forEach((zone) => {
                    if (!zone?.coordinates || zone.coordinates.length === 0 || !result) return;

                    const zonePoly = turf.polygon(zone.coordinates as any);

                    if (turf.booleanIntersects(result as any, zonePoly as any)) {
                        // @ts-ignore - version récente de Turf attend une FeatureCollection
                        const diff = turf.difference(turf.featureCollection([result as any, zonePoly as any]));

                        if (diff) {
                            result = diff as any;
                            changed = true;
                        } else {
                            // La zone est entièrement recouverte par les autres zones
                            result = null;
                        }
                    }
                });

                if (!result) {
                    alert("La nouvelle zone est entièrement recouverte par les zones existantes.");
                    return;
                }

                if (changed) {
                    // On remplace la géométrie initiale par la géométrie ajustée
                    // @ts-ignore
                    feature = result;
                }
            }

            if (feature?.geometry?.type === 'Polygon') {
                // @ts-ignore
                onSave({
                    type: 'Polygon',
                    coordinates: feature.geometry.coordinates
                });
            } else if (feature?.geometry?.type === 'MultiPolygon') {
                // @ts-ignore
                onSave({
                    type: 'Polygon',
                    coordinates: (feature.geometry as any).coordinates
                });
            }

            onClose();
        } else {
            onSave(null);
            onClose();
        }
    };

    const deleteFeature = () => {
        if (contextMenu && draw.current) {
            draw.current.delete(contextMenu.featureId);
            setContextMenu(null);
        }
    };

    const createHole = () => {
        if (contextMenu && draw.current) {
            console.log("Déduire: Début de l'opération");
            const holeId = contextMenu.featureId;
            const all = draw.current.getAll();
            // @ts-ignore
            const holeFeature = all.features.find(f => f.id === holeId);
            // @ts-ignore
            const otherFeatures = all.features.filter(f => f.id !== holeId);

            if (holeFeature && otherFeatures.length > 0) {
                let modified = false;
                const newFeatures = [];

                otherFeatures.forEach(f => {
                    try {
                        const poly1 = f.geometry.type === 'Polygon' ? turf.polygon(f.geometry.coordinates) : turf.multiPolygon(f.geometry.coordinates);
                        const poly2 = holeFeature.geometry.type === 'Polygon' ? turf.polygon(holeFeature.geometry.coordinates) : turf.multiPolygon(holeFeature.geometry.coordinates);

                        if (turf.booleanIntersects(poly1, poly2)) {
                            console.log("Intersection trouvée, calcul de la différence...");
                            // Correction : passage d'une FeatureCollection pour la version récente de Turf
                            // @ts-ignore
                            const diff = turf.difference(turf.featureCollection([poly1 as any, poly2 as any]));
                            
                            if (diff) {
                                console.log("Différence calculée avec succès");
                                modified = true;
                                newFeatures.push(diff);
                            } else {
                                console.log("Différence nulle (zone entièrement effacée ?)");
                            }
                        } else {
                            newFeatures.push(f);
                        }
                    } catch (e) {
                        console.error("Erreur lors de la différence Turf", e);
                        newFeatures.push(f);
                    }
                });

                if (modified) {
                    console.log("Mise à jour de la carte : suppression de tout et ajout des nouvelles zones");
                    draw.current.deleteAll();
                    if (newFeatures.length > 0) {
                        draw.current.add({
                            type: 'FeatureCollection',
                            features: newFeatures as any
                        });
                    }
                } else {
                    console.log("Aucune modification (pas d'intersection trouvée)");
                }
            }
            setContextMenu(null);
        }
    }

    const generateIsochrone = async () => {
        if (!selectedShop?.location?.coordinates) {
            alert("Le shop n'a pas de localisation définie.");
            return;
        }
        
        const apiKey = getEnv('MAPBOX_API_KEY');
        const [lng, lat] = selectedShop.location.coordinates;
        let minutes = parseInt(isochroneValue, 10);

        if (generationType === 'distance') {
            // Approximation distance -> temps
            // Vitesse moyenne estimée en ville : 30 km/h
            // 30 km = 60 min => 1 km = 2 min
            const speedKmh = 30;
            const km = parseFloat(isochroneValue);
            minutes = Math.round((km / speedKmh) * 60);
            
            if (minutes < 1) minutes = 1;
            if (minutes > 60) minutes = 60; // Mapbox limite à 60 min
        }

        const url = `https://api.mapbox.com/isochrone/v1/mapbox/${isochroneMode}/${lng},${lat}?contours_minutes=${minutes}&polygons=true&access_token=${apiKey}`;

        try {
            const response = await fetch(url);
            const data = await response.json();
            if (data.features && data.features.length > 0) {
                draw.current?.add(data.features[0]);
                setShowIsochroneMenu(false);
            }
        } catch (e) {
            console.error("Erreur isochrone", e);
            alert("Erreur lors de la génération de la zone automatique.");
        }
    };

    if (!isOpen) return null;

    const content = (
        <View style={styles.container}>
             <View style={styles.header}>
                 <Text style={styles.title}>Éditeur de zone</Text>
                 <View style={styles.actions}>
                    {/* Barre d'outils Mode */}
                    <View style={styles.toolbar}>
                        <TouchableOpacity 
                            style={[styles.toolBtn, drawMode === 'simple_select' && styles.toolBtnActive]} 
                            onPress={() => changeMode('simple_select')}
                        >
                            <Text style={[styles.toolBtnText, drawMode === 'simple_select' && styles.toolBtnTextActive]}>✋ Sélection</Text>
                        </TouchableOpacity>
                        <TouchableOpacity 
                            style={[styles.toolBtn, drawMode === 'draw_polygon' && styles.toolBtnActive]} 
                            onPress={() => changeMode('draw_polygon')}
                        >
                            <Text style={[styles.toolBtnText, drawMode === 'draw_polygon' && styles.toolBtnTextActive]}>✏️ Tracer</Text>
                        </TouchableOpacity>
                    </View>

                    <View style={{ width: 20 }} />

                    <TouchableOpacity 
                        style={[styles.toolBtn, showIsochroneMenu && styles.toolBtnActive]} 
                        onPress={() => setShowIsochroneMenu(!showIsochroneMenu)}
                    >
                        <Text style={[styles.toolBtnText, showIsochroneMenu && styles.toolBtnTextActive]}>⚡️ Zone Auto</Text>
                    </TouchableOpacity>
                    
                    <View style={{ width: 20 }} />
                    {/* <Button title="Annuler" onPress={onClose} bordered /> */}
                    <View style={{ width: 10 }} />
                    <Button title="Valider" onPress={handleSave} />
                 </View>
             </View>

             {/* Popover Zone Auto */}
             {showIsochroneMenu && (
                 <View style={styles.isochroneMenu}>
                     <Text style={styles.isoTitle}>Générer une zone</Text>
                     
                     {/* Tabs Type */}
                     <View style={styles.tabs}>
                        <TouchableOpacity 
                            style={[styles.tab, generationType === 'time' && styles.tabActive]}
                            onPress={() => setGenerationType('time')}
                        >
                            <Text style={[styles.tabText, generationType === 'time' && styles.tabTextActive]}>Temps</Text>
                        </TouchableOpacity>
                        <TouchableOpacity 
                            style={[styles.tab, generationType === 'distance' && styles.tabActive]}
                            onPress={() => setGenerationType('distance')}
                        >
                            <Text style={[styles.tabText, generationType === 'distance' && styles.tabTextActive]}>Distance</Text>
                        </TouchableOpacity>
                     </View>

                     {/* Modes transport (uniquement si Temps, ou pour ajuster la vitesse implicite si Distance ?) 
                         Pour simplifier, on garde le mode transport car ça influe sur l'isochrone final. 
                     */}
                     <View style={styles.isoRow}>
                         <TouchableOpacity onPress={() => setIsochroneMode('driving')} style={[styles.modeBtn, isochroneMode === 'driving' && styles.modeBtnActive]}><Text>Voiture</Text></TouchableOpacity>
                         <TouchableOpacity onPress={() => setIsochroneMode('cycling')} style={[styles.modeBtn, isochroneMode === 'cycling' && styles.modeBtnActive]}><Text>Vélo</Text></TouchableOpacity>
                     </View>

                     <View style={styles.isoInputContainer}>
                        <Text>{generationType === 'time' ? 'Durée (min)' : 'Distance (km)'}: </Text>
                        <input 
                            type="number" 
                            value={isochroneValue} 
                            onChange={(e) => setIsochroneValue(e.target.value)}
                            style={styles.htmlInput}
                        />
                     </View>
                     <Button title="Générer" onPress={generateIsochrone} grow />
                 </View>
             )}

             {/* Menu Contextuel */}
             {contextMenu && (
                 <View style={[styles.contextMenu, { left: contextMenu.x, top: contextMenu.y }]}>
                     <TouchableOpacity style={styles.contextMenuItem} onPress={deleteFeature}>
                         <Text style={styles.contextMenuText}>Supprimer la zone</Text>
                     </TouchableOpacity>
                     <TouchableOpacity style={styles.contextMenuItem} onPress={createHole}>
                         <Text style={styles.contextMenuText}>Déduire (créer un trou)</Text>
                     </TouchableOpacity>
                 </View>
             )}

             {React.createElement('div', { ref: mapContainer, style: { flex: 1, width: '100%', height: '100%' } })}
        </View>
    );

    if (Platform.OS === 'web' && typeof document !== 'undefined') {
        return ReactDOM.createPortal(content, document.body);
    }

    return content;
};

const styles = StyleSheet.create({
    container: {
        // @ts-ignore
        position: Platform.OS === 'web' ? 'fixed' : 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 99999,
        backgroundColor: 'white',
        flex: 1,
        height: '100vh',
        width: '100vw',
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: 16,
        borderBottomWidth: 1,
        borderBottomColor: '#e0e0e0',
        backgroundColor: 'white',
        zIndex: 10,
    },
    title: {
        fontSize: 18,
        fontWeight: 'bold',
    },
    actions: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    toolbar: {
        flexDirection: 'row',
        backgroundColor: '#f5f5f5',
        borderRadius: 8,
        padding: 4,
    },
    toolBtn: {
        paddingHorizontal: 12,
        paddingVertical: 8,
        borderRadius: 6,
        marginRight: 5,
    },
    toolBtnActive: {
        backgroundColor: theme.colors.celadonblue,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.2,
        shadowRadius: 1.41,
        elevation: 2,
    },
    toolBtnText: {
        fontWeight: '600',
        color: '#555',
        fontSize: 14,
    },
    toolBtnTextActive: {
        color: 'white',
    },
    isochroneMenu: {
        position: 'absolute',
        top: 70,
        right: 20,
        backgroundColor: 'white',
        padding: 15,
        borderRadius: 8,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.25,
        shadowRadius: 3.84,
        elevation: 5,
        zIndex: 20,
        width: 280,
    },
    isoTitle: {
        fontWeight: 'bold',
        marginBottom: 15,
        fontSize: 16,
        textAlign: 'center',
    },
    tabs: {
        flexDirection: 'row',
        marginBottom: 15,
        backgroundColor: '#f0f0f0',
        borderRadius: 6,
        padding: 2,
    },
    tab: {
        flex: 1,
        paddingVertical: 6,
        alignItems: 'center',
        borderRadius: 4,
    },
    tabActive: {
        backgroundColor: 'white',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.1,
        shadowRadius: 1,
        elevation: 1,
    },
    tabText: {
        fontSize: 13,
        color: '#666',
    },
    tabTextActive: {
        fontWeight: '600',
        color: theme.colors.celadonblue,
    },
    isoRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginBottom: 15,
    },
    modeBtn: {
        paddingHorizontal: 12,
        paddingVertical: 6,
        borderWidth: 1,
        borderColor: '#ddd',
        borderRadius: 4,
        flex: 0.48,
        alignItems: 'center',
    },
    modeBtnActive: {
        backgroundColor: '#e6f7ff',
        borderColor: theme.colors.celadonblue,
    },
    isoInputContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        marginBottom: 15,
        justifyContent: 'space-between',
    },
    htmlInput: {
        padding: 8,
        borderWidth: 1,
        borderColor: '#ccc',
        borderRadius: 4,
        width: 80,
        textAlign: 'center',
    },
    contextMenu: {
        position: 'absolute',
        backgroundColor: 'white',
        borderRadius: 4,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.25,
        shadowRadius: 3.84,
        elevation: 5,
        zIndex: 100, 
        width: 180,
    },
    contextMenuItem: {
        padding: 12,
        borderBottomWidth: 1,
        borderBottomColor: '#f0f0f0',
    },
    contextMenuText: {
        fontSize: 14,
        color: '#333',
    }
});
